import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  UploadSimple,
  Trash,
  ArrowCounterClockwise,
  Plus,
  ImageSquare,
  Check,
} from "@phosphor-icons/react";
import AdminLayout from "@/pages/AdminLayout";
import {
  fetchFlakes,
  adminUploadFlake,
  adminDeleteFlake,
  adminRestoreFlake,
  getToken,
  API,
} from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const BLEND_OPTIONS = ["overlay", "multiply", "hard-light", "soft-light"];

export default function AdminFlakes() {
  const [flakes, setFlakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchFlakes();
      setFlakes(data);
    } catch {
      toast.error("Failed to load flakes.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const removeFlake = async (id) => {
    if (!window.confirm("Remove this flake from the visualizer?")) return;
    try {
      await adminDeleteFlake(id);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  const restoreFlake = async (id) => {
    try {
      await adminRestoreFlake(id);
      toast.success("Restored");
      load();
    } catch {
      toast.error("Restore failed");
    }
  };

  return (
    <AdminLayout active="flakes">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline">Catalog</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">
            Flake blends
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Upload photos of your actual flake products. Customers will see
            them in the visualizer.
          </p>
        </div>
        <Button
          onClick={() => setShowForm((s) => !s)}
          className="text-white font-semibold"
          style={{ background: "var(--epx-primary)" }}
          data-testid="add-flake-toggle"
        >
          <Plus size={16} className="mr-2" />
          {showForm ? "Close uploader" : "Add new flake"}
        </Button>
      </div>

      {showForm && (
        <UploadForm
          onUploaded={() => {
            setShowForm(false);
            load();
          }}
        />
      )}

      {loading ? (
        <div className="py-16 text-center text-zinc-500">Loading flakes…</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {flakes.map((f) => (
            <FlakeCard
              key={f.id}
              flake={f}
              onRemove={() => removeFlake(f.id)}
            />
          ))}
          <RestoreCard onRestore={restoreFlake} currentIds={flakes.map((f) => f.id)} />
        </div>
      )}
    </AdminLayout>
  );
}

function FlakeCard({ flake, onRemove }) {
  const src = flake.image?.startsWith("/api/")
    ? `${API.replace(/\/api$/, "")}${flake.image}`
    : flake.image;
  return (
    <div
      className="rounded-xl overflow-hidden border group relative"
      style={{
        background: "var(--epx-surface)",
        borderColor: "var(--epx-border)",
      }}
      data-testid={`admin-flake-${flake.id}`}
    >
      <div
        className="aspect-square bg-black/50"
        style={{
          backgroundImage: `url("${src}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="font-heading font-semibold truncate">{flake.name}</p>
          {flake.is_default ? (
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">
              Default
            </span>
          ) : (
            <span
              className="text-[10px] uppercase tracking-wider"
              style={{ color: "var(--epx-primary)" }}
            >
              Custom
            </span>
          )}
        </div>
        {flake.description && (
          <p className="text-xs text-zinc-400 mt-1 line-clamp-2">
            {flake.description}
          </p>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="w-full mt-2 text-zinc-300 hover:text-red-300 hover:bg-red-500/10"
          data-testid={`remove-flake-${flake.id}`}
        >
          <Trash size={13} className="mr-1.5" />
          {flake.is_default ? "Hide from customers" : "Delete"}
        </Button>
      </div>
    </div>
  );
}

function RestoreCard({ onRestore, currentIds }) {
  // List default flake IDs to give the admin a one-click restore for any hidden ones.
  // Doing it client-side is fine because the server tracks the truth.
  const DEFAULTS = ["domino", "granite", "coastal", "tuxedo", "desert", "cabin"];
  const hidden = DEFAULTS.filter((id) => !currentIds.includes(id));
  if (hidden.length === 0) return null;
  return (
    <div
      className="rounded-xl border border-dashed p-4 flex flex-col"
      style={{
        background: "rgba(255,255,255,0.02)",
        borderColor: "var(--epx-border)",
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <ArrowCounterClockwise size={18} style={{ color: "var(--epx-primary)" }} />
        <p className="font-heading font-semibold text-sm">Restore hidden</p>
      </div>
      <p className="text-xs text-zinc-400 mb-3">
        Defaults you&apos;ve removed:
      </p>
      <div className="flex flex-wrap gap-1.5">
        {hidden.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => onRestore(id)}
            className="text-xs px-2 py-1 rounded-md border border-white/10 text-zinc-300 hover:bg-white/5"
            data-testid={`restore-${id}`}
          >
            {id}
          </button>
        ))}
      </div>
    </div>
  );
}

function UploadForm({ onUploaded }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [blend, setBlend] = useState("overlay");
  const [prompt, setPrompt] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const onPick = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file.");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Image too large (max 8MB).");
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error("Choose an image first.");
      return;
    }
    if (!name.trim()) {
      toast.error("Give the flake a name.");
      return;
    }
    if (!getToken()) {
      toast.error("Session expired — sign in again.");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", name);
      fd.append("description", description);
      fd.append("blend_mode", blend);
      fd.append(
        "prompt_keywords",
        prompt ||
          `${name} epoxy flake chips broadcast densely on the floor`,
      );
      await adminUploadFlake(fd);
      toast.success(`${name} added`);
      setName("");
      setDescription("");
      setPrompt("");
      setFile(null);
      setPreview(null);
      onUploaded();
    } catch (e) {
      const m = e?.response?.data?.detail || "Upload failed";
      toast.error(typeof m === "string" ? m : "Upload failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="rounded-2xl border p-5 sm:p-6 mb-6 grid grid-cols-1 lg:grid-cols-12 gap-5"
      style={{
        background: "var(--epx-surface)",
        borderColor: "var(--epx-border)",
      }}
      data-testid="flake-upload-form"
    >
      <div className="lg:col-span-5">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => onPick(e.target.files?.[0])}
          data-testid="flake-file-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="aspect-square w-full rounded-xl border-2 border-dashed flex flex-col items-center justify-center text-center p-6 transition-colors"
          style={{
            borderColor: preview ? "transparent" : "rgba(255,255,255,0.15)",
            backgroundImage: preview ? `url("${preview}")` : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
          data-testid="flake-file-button"
        >
          {!preview && (
            <>
              <UploadSimple
                size={26}
                weight="duotone"
                style={{ color: "var(--epx-primary)" }}
              />
              <p className="font-heading text-base font-semibold mt-3">
                Upload flake photo
              </p>
              <p className="text-xs text-zinc-400 mt-1">
                Top-down close-up of the actual flake blend works best.
              </p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-2 font-mono-tech">
                JPG · PNG · WEBP · up to 8MB
              </p>
            </>
          )}
        </button>
      </div>
      <div className="lg:col-span-7 space-y-4">
        <div>
          <Label htmlFor="flake-name" className="text-zinc-300">
            Flake name *
          </Label>
          <Input
            id="flake-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder='e.g. "Midnight Granite"'
            className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
            data-testid="flake-name-input"
          />
        </div>
        <div>
          <Label htmlFor="flake-desc" className="text-zinc-300">
            Short description
          </Label>
          <Input
            id="flake-desc"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="One line shown under the swatch in the visualizer."
            className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
            data-testid="flake-desc-input"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-zinc-300">Blend mode</Label>
            <select
              value={blend}
              onChange={(e) => setBlend(e.target.value)}
              className="w-full mt-1.5 bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm text-white"
              data-testid="flake-blend-select"
            >
              {BLEND_OPTIONS.map((b) => (
                <option key={b} value={b} style={{ color: "#0a0a0a" }}>
                  {b}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-10 text-white font-semibold"
              style={{
                background: "var(--epx-primary)",
                boxShadow: "0 0 20px rgba(var(--epx-primary-rgb),0.30)",
              }}
              data-testid="flake-upload-submit"
            >
              {submitting ? (
                "Uploading…"
              ) : (
                <>
                  <Check size={14} className="mr-1.5" /> Save flake
                </>
              )}
            </Button>
          </div>
        </div>
        <div>
          <Label htmlFor="flake-prompt" className="text-zinc-300">
            AI hint (optional)
          </Label>
          <Textarea
            id="flake-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the colors to help the AI render — e.g. 'deep charcoal base with copper and cream flake chips'."
            rows={2}
            className="bg-black/30 border-white/10 text-white placeholder:text-zinc-500"
            data-testid="flake-prompt-input"
          />
          <p className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1">
            <ImageSquare size={11} /> Used only for the AI Realistic Render
            prompt.
          </p>
        </div>
      </div>
    </form>
  );
}
